import { useAppConfig } from '@/hooks/useAppConfig';

interface RatioModalProps {
  isOpen: boolean;
  selectedId: string | null;
  onClose: () => void;
  onSelect: (ratioId: string) => void;
  allowedRatioIds?: string[];
}

export default function RatioModal({ isOpen, selectedId, onClose, onSelect, allowedRatioIds }: RatioModalProps) {
  const config = useAppConfig();
  const { t } = useTranslation();
  const ratios = (config?.ratios ?? []).filter((ratio) => (
    !allowedRatioIds || allowedRatioIds.includes(ratio.id)
  ));

  if (!isOpen) return null;

  const getPixelShape = (w: number, h: number) => {
    const maxDim = 36;
    if (w >= h) {
      return { width: maxDim, height: Math.round((maxDim * h) / w) };
    }
    return { width: Math.round((maxDim * w) / h), height: maxDim };
  };

  return (
    <div data-testid="ratio-modal" className="fixed inset-0 z-50 flex items-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full bg-white rounded-t-3xl max-h-[70vh] overflow-hidden animate-slide-up">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="text-lg font-bold text-gray-800">{t('gen_selectRatio')}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="px-5 space-y-2" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
          {ratios.map((ratio) => {
            const isSelected = selectedId === ratio.id;
            const shape = getPixelShape(ratio.width, ratio.height);

            return (
              <button
                key={ratio.id}
                data-testid={`ratio-item-${ratio.id}`}
                onClick={() => { onSelect(ratio.id); onClose(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all cursor-pointer active:scale-[0.98] ${
                  isSelected ? 'bg-pink-50 ring-2 ring-pink-400' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                  <div
                    className={`rounded border-2 ${isSelected ? 'border-pink-400' : 'border-gray-300'}`}
                    style={{
                      width: shape.width,
                      height: shape.height,
                      background: isSelected ? 'rgba(255,107,157,0.1)' : undefined,
                    }}
                  />
                </div>

                <div className="flex-1 text-left">
                  <span className={`text-sm font-semibold ${isSelected ? 'text-pink-600' : 'text-gray-700'}`}>
                    {ratio.label}
                  </span>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {ratio.width} × {ratio.height} {t('gen_pixels')}
                  </p>
                </div>

                {isSelected && <i className="ri-check-line text-pink-500 text-lg flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
